import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { Badge, Card, Empty, List, Spin, Typography, message } from 'antd';
import EventCard from './EventCard';
import eventService from '../services/eventService';
import { loadMechanicStatuses } from '../services/mechanicStatusService';
import '../assets/styles/eventList.css';

const { Text, Title } = Typography;
const EMPTY_FILTERS = [];

const EventBoard = ({ title, count, loading, events, emptyText, mechanicStatuses }) => (
  <Card className="event-board" variant="outlined">
    <div className="event-board__header">
      <Title level={5} className="event-board__title">{title}</Title>
      <Badge count={count} showZero overflowCount={9999} />
    </div>
    {loading ? (
      <div className="event-board__loading">
        <Spin />
      </div>
    ) : events.length === 0 ? (
      <Empty description={emptyText} image={Empty.PRESENTED_IMAGE_SIMPLE} />
    ) : (
      <List
        dataSource={events}
        renderItem={(event) => (
          <List.Item className="event-list__item">
            <EventCard event={event} mechanicStatuses={mechanicStatuses} />
          </List.Item>
        )}
      />
    )}
  </Card>
);

const EventList = forwardRef(({
  searchTerm = '',
  eventTypeIds = EMPTY_FILTERS,
  eventInvolvementFilters = EMPTY_FILTERS,
  eventStatusIds = EMPTY_FILTERS,
  eventLocationIds = EMPTY_FILTERS,
  eventProviderStatusIds = EMPTY_FILTERS
}, ref) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [mechanicStatuses, setMechanicStatuses] = useState([]);
  const hasLoadedRef = useRef(false);

  const fetchEvents = useCallback(async () => {
    try {
      if (!hasLoadedRef.current) {
        setLoading(true);
      }
      setRefreshing(true);
      const response = await eventService.getActiveEvents({
        searchTerm,
        eventTypeIds,
        eventInvolvementFilters,
        eventStatusIds,
        eventLocationIds,
        eventProviderStatusIds
      });
      setEvents(Array.isArray(response?.items) ? response.items : []);
    } catch (error) {
      console.error('Error fetching active events:', error);
      message.error('An error occurred while fetching events');
    } finally {
      hasLoadedRef.current = true;
      setLoading(false);
      setRefreshing(false);
    }
  }, [
    searchTerm,
    eventTypeIds,
    eventInvolvementFilters,
    eventStatusIds,
    eventLocationIds,
    eventProviderStatusIds
  ]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    loadMechanicStatuses().then(setMechanicStatuses);
  }, []);

  useImperativeHandle(ref, () => ({
    refresh: fetchEvents
  }), [fetchEvents]);

  const rows = useMemo(() => events || [], [events]);

  return (
    <div className="event-list-container">
      {refreshing && !loading ? (
        <div className="event-list__refreshing">
          <Spin size="small" />
          <Text type="secondary">Refreshing events...</Text>
        </div>
      ) : null}

      <div className="event-list__boards">
          <EventBoard
            title="All Events"
            count={rows.length}
            loading={loading}
            events={rows}
            emptyText="No events found"
            mechanicStatuses={mechanicStatuses}
          />
      </div>
    </div>
  );
});

export default EventList;
